import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLogin from './AdminLogin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Chip,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Pagination,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { Order, OrderStatus, PaymentMethod } from '../types';
import { getOrders, getCatalog, updateOrderAdmin, markOrderAsPaid, uploadOrderFiles } from '../api';
import { SubjectData, calculateFullCoursePrice, calculateSelectedWorksPrice } from '../data/subjects';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

const statusLabels: Partial<Record<OrderStatus, string>> = {
  [OrderStatus.NEW]: 'Новый',
  [OrderStatus.WAITING_PAYMENT]: 'Ожидание оплаты',
  [OrderStatus.PAID]: 'Оплачен',
  [OrderStatus.IN_PROGRESS]: 'В работе',
  [OrderStatus.COMPLETED]: 'Выполнен',
};

const allowedStatuses: OrderStatus[] = [
  OrderStatus.NEW,
  OrderStatus.WAITING_PAYMENT,
  OrderStatus.PAID,
  OrderStatus.IN_PROGRESS,
  OrderStatus.COMPLETED,
];

const statusChip: Partial<Record<OrderStatus, { bg: string; color: string }>> = {
  [OrderStatus.NEW]: { bg: 'rgba(148,163,184,0.2)', color: '#475569' },
  [OrderStatus.WAITING_PAYMENT]: { bg: 'rgba(234,179,8,0.2)', color: '#a16207' },
  [OrderStatus.PAID]: { bg: 'rgba(14,165,233,0.2)', color: '#0ea5e9' },
  [OrderStatus.IN_PROGRESS]: { bg: 'rgba(99,102,241,0.2)', color: '#6366f1' },
  [OrderStatus.COMPLETED]: { bg: 'rgba(16,185,129,0.2)', color: '#059669' },
};

const rowBg: Partial<Record<OrderStatus, string>> = {
  [OrderStatus.NEW]: 'rgba(148,163,184,0.06)',
  [OrderStatus.WAITING_PAYMENT]: 'rgba(234,179,8,0.06)',
  [OrderStatus.PAID]: 'rgba(14,165,233,0.06)',
  [OrderStatus.IN_PROGRESS]: 'rgba(99,102,241,0.06)',
  [OrderStatus.COMPLETED]: 'rgba(16,185,129,0.08)',
};

const paymentMethodLabels: Record<PaymentMethod, string> = {
  sberbank: 'СБЕРБАНК',
  ozonbank: 'ОЗОНБАНК',
  alfabank: 'АЛЬФАБАНК',
  cash: 'НАЛИЧНЫЕ',
};

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('adminAuth') === 'true'
  );
  const [orders, setOrders] = useState<Order[]>([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<OrderStatus>(OrderStatus.NEW);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [priceInput, setPriceInput] = useState<string>('');
  const [payoutInput, setPayoutInput] = useState<string>('');
  const [executorInput, setExecutorInput] = useState<string>('');
  const [titleInput, setTitleInput] = useState<string>('');
  const [descriptionInput, setDescriptionInput] = useState<string>('');
  const [inputDataInput, setInputDataInput] = useState<string>('');
  const [variantInfoInput, setVariantInfoInput] = useState<string>('');
  const [deadlineInput, setDeadlineInput] = useState<string>('');
  const [studentNameInput, setStudentNameInput] = useState<string>('');
  const [studentGroupInput, setStudentGroupInput] = useState<string>('');
  const [paymentMethodInput, setPaymentMethodInput] = useState<PaymentMethod>('sberbank');

  // Каталог предметов/работ — для выбора состава практических работ заказа
  // мини-меню с чекбоксами, как при оформлении заказа, вместо текстового поля.
  const [catalogSubjects, setCatalogSubjects] = useState<SubjectData[]>([]);
  const [selectedWorksInput, setSelectedWorksInput] = useState<string[]>([]);
  const [isFullCourseInput, setIsFullCourseInput] = useState(false);

  useEffect(() => {
    getCatalog()
      .then(catalog => setCatalogSubjects(catalog.subjects))
      .catch(e => console.error('Ошибка загрузки каталога работ:', e));
  }, []);

  const loadOrders = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      const response = await getOrders(page, rowsPerPage);
      setOrders(response.orders);
      setTotalOrders(response.total || 0);
    } catch (error) {
      console.error('Ошибка загрузки заказов:', error);
      setError('Не удалось загрузить заказы');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, page, rowsPerPage]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const pageCount = Math.max(1, Math.ceil(totalOrders / rowsPerPage));

  const getDefaultPayout = (order: Order) => {
    const base = order.actual_price ?? order.subject?.price ?? 0;
    return base ? Math.round(base * 0.8 * 100) / 100 : 0;
  };

  const handleOrderClick = (order: Order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setDialogOpen(true);
    const initialPrice = (order.actual_price ?? order.subject?.price ?? 0).toString();
    setPriceInput(initialPrice);
    const fallbackPayout = getDefaultPayout(order);
    const initialPayout = (order.payout_amount ?? fallbackPayout).toString();
    setPayoutInput(initialPayout);
    setExecutorInput(order.executor_telegram ? `@${order.executor_telegram}` : '');
    setTitleInput(order.title || '');
    setDescriptionInput(order.description || '');
    setInputDataInput(order.input_data || '');
    setVariantInfoInput(order.variant_info || '');
    setDeadlineInput(order.deadline ? order.deadline.split('T')[0] : '');
    setStudentNameInput(order.student?.name || '');
    setStudentGroupInput(order.student?.group || '');
    setPaymentMethodInput(order.payment_method || 'sberbank');

    let parsedSelectedWorks: string[] = [];
    if (order.selected_works) {
      try {
        const parsed = JSON.parse(order.selected_works);
        if (Array.isArray(parsed)) {
          parsedSelectedWorks = parsed;
        }
      } catch (e) {
        console.warn('Не удалось разобрать selected_works заказа:', e);
      }
    }
    setSelectedWorksInput(parsedSelectedWorks);
    setIsFullCourseInput(Boolean(order.is_full_course));
  };

  const getMatchedCatalogSubject = (order: Order | null): SubjectData | undefined => {
    if (!order?.subject?.name) return undefined;
    return catalogSubjects.find(s => s.name === order.subject?.name);
  };

  const toggleSelectedWork = (workId: string) => {
    setSelectedWorksInput(prev =>
      prev.includes(workId) ? prev.filter(id => id !== workId) : [...prev, workId]
    );
  };

  const buildWorksDescription = (subject: SubjectData): string => {
    if (isFullCourseInput) {
      return 'Заказан весь курс';
    }
    return subject.works
      .filter(work => selectedWorksInput.includes(work.id))
      .map(work => work.title)
      .join(', ');
  };

  const handleSaveAdmin = async (statusOverride?: OrderStatus) => {
    if (!selectedOrder || !selectedOrder.id) return;

    try {
      const parsedPrice = parseFloat(priceInput.replace(',', '.'));
      if (isNaN(parsedPrice) || parsedPrice < 0) {
        setError('Введите корректную стоимость');
        return;
      }

      let parsedPayout: number | null = null;
      if (payoutInput.trim() === '') {
        parsedPayout = getDefaultPayout(selectedOrder);
      } else {
        parsedPayout = parseFloat(payoutInput.replace(',', '.'));
        if (isNaN(parsedPayout) || parsedPayout < 0) {
          setError('Введите корректную сумму к выплате');
          return;
        }
      }

      const matchedSubject = getMatchedCatalogSubject(selectedOrder);

      const payload: any = {
        input_data: inputDataInput,
        variant_info: variantInfoInput,
        deadline: deadlineInput,
        student_name: studentNameInput,
        student_group: studentGroupInput,
        actual_price: parsedPrice,
        payout_amount: parsedPayout,
        executor_telegram: executorInput.trim() ? executorInput.trim().replace('@', '') : null,
        status: statusOverride || newStatus,
        is_paid: selectedOrder.is_paid,
        payment_method: paymentMethodInput,
      };

      if (matchedSubject && matchedSubject.works.length > 0) {
        // Состав работ редактируется мини-меню с выбором — описание пересчитываем из каталога
        payload.selected_works = isFullCourseInput ? [] : selectedWorksInput;
        payload.is_full_course = isFullCourseInput;
        payload.description = buildWorksDescription(matchedSubject);
      } else {
        // Нет соответствия в каталоге (кастомный заказ) — описание остаётся свободным текстом
        payload.description = descriptionInput;
      }

      const updatedOrder = await updateOrderAdmin(selectedOrder.id, payload);
      setOrders(prev => prev.map(order => 
        order.id === updatedOrder.id ? updatedOrder : order
      ));
      setSelectedOrder(updatedOrder);
      setDialogOpen(false);
    } catch (error) {
      console.error('Ошибка обновления заказа:', error);
      setError('Не удалось сохранить изменения заказа');
    }
  };

  const handleSetWaitingPayment = async () => {
    if (!selectedOrder || !selectedOrder.id) return;
    setNewStatus(OrderStatus.WAITING_PAYMENT);
    await handleSaveAdmin(OrderStatus.WAITING_PAYMENT);
  };

  const handleMarkAsPaid = async () => {
    if (!selectedOrder || !selectedOrder.id) return;

    try {
      const updatedOrder = await markOrderAsPaid(selectedOrder.id);
      setOrders(prev => prev.map(order => 
        order.id === updatedOrder.id ? updatedOrder : order
      ));
      setSelectedOrder(updatedOrder);
    } catch (error) {
      console.error('Ошибка отметки оплаты:', error);
      setError('Не удалось отметить заказ как оплаченный');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedOrder || !selectedOrder.id || !event.target.files) return;

    try {
      setUploading(true);
      const updatedOrder = await uploadOrderFiles(selectedOrder.id, event.target.files);
      setOrders(prev => prev.map(order => 
        order.id === updatedOrder.id ? updatedOrder : order
      ));
      setSelectedOrder(updatedOrder);
    } catch (error) {
      console.error('Ошибка загрузки файлов:', error);
      setError('Не удалось загрузить файлы');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 4, background: '#ffffff' }}>
      {/* Header */}
      <Box
        sx={{
          background: '#ffffff',
          borderRadius: 4,
          p: 3,
          mb: 4,
          border: '1px solid #e2e8f0',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: '#1e293b',
            }}
          >
            Панель администратора
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: 'grey.600',
              fontWeight: 500,
              fontSize: '1.1rem',
            }}
          >
            Управление заказами и мониторинг системы
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/add')} sx={{ whiteSpace: 'nowrap' }}>
          Практические работы
        </Button>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4,
            borderRadius: 2,
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            backdropFilter: 'blur(10px)',
            '& .MuiAlert-message': {
              fontWeight: 500,
            }
          }} 
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ mb: 6 }}>
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 700, 
            mb: 3,
            color: 'grey.800',
          }}
        >
          Статистика
        </Typography>
        
        <Box 
          display="flex" 
          gap={3} 
          sx={{ 
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'stretch'
          }}
        >
          <Card 
            sx={{
              background: 'rgba(37, 99, 235, 0.05)',
              border: '1px solid rgba(37, 99, 235, 0.2)',
              borderRadius: 4,
              flex: 1,
              minWidth: { xs: 'auto', md: 200 },
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: '#2563eb',
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3, px: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#2563eb', mb: 1 }}>
                {totalOrders}
              </Typography>
              <Typography variant="h6" sx={{ color: 'grey.700', fontWeight: 600 }}>
                Всего заказов
              </Typography>
            </CardContent>
          </Card>
          
          <Card 
            sx={{
              background: 'rgba(16, 185, 129, 0.05)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: 4,
              flex: 1,
              minWidth: { xs: 'auto', md: 200 },
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: '#10b981',
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3, px: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#10b981', mb: 1 }}>
                {orders.filter(o => o.status === OrderStatus.COMPLETED).length}
              </Typography>
              <Typography variant="h6" sx={{ color: 'grey.700', fontWeight: 600 }}>
                Выполнено на странице
              </Typography>
            </CardContent>
          </Card>
          
          <Card 
            sx={{
              background: 'rgba(245, 158, 11, 0.05)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              borderRadius: 4,
              flex: 1,
              minWidth: { xs: 'auto', md: 200 },
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: '#f59e0b',
              }
            }}
          >
            <CardContent sx={{ textAlign: 'center', py: 3, px: 2 }}>
              <Typography variant="h3" sx={{ fontWeight: 700, color: '#f59e0b', mb: 1 }}>
                {orders.filter(o => o.status === OrderStatus.IN_PROGRESS).length}
              </Typography>
              <Typography variant="h6" sx={{ color: 'grey.700', fontWeight: 600 }}>
                В работе на странице
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Card 
        sx={{
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          borderRadius: 4,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700, 
              mb: 3,
              color: 'grey.800',
            }}
          >
            Все заказы ({totalOrders})
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Показаны {orders.length} заказов на странице {page} из {pageCount}
          </Typography>

          {/* Статистика по статусам текущей страницы */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {allowedStatuses.map((status) => (
              <Grid item xs={12} sm={6} md={3} lg={2} key={status}>
                <Card
                  sx={{
                    border: '1px solid #e2e8f0',
                    background: 'rgba(37,99,235,0.04)',
                  }}
                >
                  <CardContent sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {statusLabels[status]}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#2563eb' }}>
                      {orders.filter(o => o.status === status).length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          
          <TableContainer 
            component={Paper}
            sx={{
              borderRadius: 4,
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
              overflowX: 'auto',
            }}
          >
            <Table sx={{ minWidth: 600, tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow
                  sx={{
                    background: '#2563eb',
                    '& .MuiTableCell-head': {
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      color: '#ffffff',
                      borderBottom: '2px solid #1e40af',
                      py: 2,
                      px: 2,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                    }
                  }}
                >
                  <TableCell>ID</TableCell>
                  <TableCell>Студент</TableCell>
                  <TableCell>Группа</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>Название</TableCell>
                  <TableCell>Предмет</TableCell>
                  <TableCell>Дедлайн</TableCell>
                  <TableCell>Цена</TableCell>
                  <TableCell>К выплате</TableCell>
                  <TableCell>Исполнитель</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Оплата</TableCell>
                  <TableCell>Создан</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order, index) => (
                  <TableRow
                    key={order.id}
                    onClick={() => handleOrderClick(order)}
                    sx={{ 
                      cursor: 'pointer',
                      background: rowBg[order.status] || (index % 2 === 0 ? '#ffffff' : 'rgba(37, 99, 235, 0.02)'),
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: rowBg[order.status] || 'rgba(37, 99, 235, 0.05)',
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: '1px solid #e2e8f0',
                        py: 2,
                        px: 2,
                        fontWeight: 500,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                      }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 700, color: '#2563eb', whiteSpace: 'nowrap' }}>
                      #{order.id}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#374151' }}>
                      {order.student?.name}
                    </TableCell>
                    <TableCell>{order.student?.group || '-'}</TableCell>
                    <TableCell 
                      sx={{ 
                        minWidth: 160,
                        maxWidth: 240,
                        fontWeight: 600,
                        color: '#1f2937',
                        wordBreak: 'break-word',
                        whiteSpace: 'normal',
                        lineHeight: 1.4,
                      }}
                    >
                      {order.title}
                    </TableCell>
                    <TableCell>{order.subject?.name || '—'}</TableCell>
                    <TableCell>
                      {format(new Date(order.deadline), 'dd.MM.yyyy', { locale: ru })}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>
                      {order.actual_price ?? order.subject?.price ?? 0} ₽
                    </TableCell>
                    <TableCell sx={{ color: '#059669', fontWeight: 600 }}>
                      {order.payout_amount != null ? `${order.payout_amount} ₽` : `${getDefaultPayout(order)} ₽`}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {order.executor_telegram ? `@${order.executor_telegram}` : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[order.status] || order.status}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          background: statusChip[order.status]?.bg || 'rgba(37,99,235,0.1)',
                          color: statusChip[order.status]?.color || '#2563eb',
                          border: 'none',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        label={order.is_paid ? 'Да' : 'Нет'}
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.8rem',
                          background: order.is_paid
                            ? 'rgba(16, 185, 129, 0.1)'
                            : 'rgba(239, 68, 68, 0.1)',
                          color: order.is_paid ? '#059669' : '#dc2626',
                          border: `1px solid ${order.is_paid ? '#10b981' : '#ef4444'}33`,
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {order.created_at && format(new Date(order.created_at), 'dd.MM.yyyy', { locale: ru })}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>На странице</InputLabel>
              <Select
                value={String(rowsPerPage)}
                label="На странице"
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[25, 50, 100].map((value) => (
                  <MenuItem key={value} value={String(value)}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Pagination
              count={pageCount}
              page={page}
              color="primary"
              onChange={(_, value) => setPage(value)}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Диалог редактирования заказа */}
      <Dialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxHeight: '90vh',
            overflow: 'auto',
          }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          Редактирование заказа #{selectedOrder?.id}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedOrder && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Предмет: <strong>{selectedOrder.subject?.name || 'Не указан'}</strong>
              </Typography>
              
              <Typography variant="h6" gutterBottom sx={{ wordBreak: 'break-word' }}>
                {titleInput}
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Студент (ФИО)"
                    value={studentNameInput}
                    onChange={(e) => setStudentNameInput(e.target.value)}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    fullWidth
                    label="Группа"
                    value={studentGroupInput}
                    onChange={(e) => setStudentGroupInput(e.target.value)}
                    sx={{ mb: 2 }}
                  />
              <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Telegram:</strong> @{selectedOrder.student?.telegram || 'не указан'}
              </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Дедлайн"
                    InputLabelProps={{ shrink: true }}
                    value={deadlineInput}
                    onChange={(e) => setDeadlineInput(e.target.value)}
                  />
                </Grid>
              </Grid>

              {(() => {
                const matchedSubject = getMatchedCatalogSubject(selectedOrder);
                if (matchedSubject && matchedSubject.works.length > 0) {
                  return (
                    <Box sx={{ mb: 2, p: 2, border: '1px solid #e2e8f0', borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Состав практических работ
                      </Typography>

                      {matchedSubject.fullCourseDiscount != null && (
                        <FormControlLabel
                          sx={{ mb: 1, display: 'flex' }}
                          control={
                            <Checkbox
                              checked={isFullCourseInput}
                              color="success"
                              onChange={(e) => setIsFullCourseInput(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                              Весь курс ({matchedSubject.works.length} работ, скидка {matchedSubject.fullCourseDiscount}%)
                            </Typography>
                          }
                        />
                      )}

                      {!isFullCourseInput && (
                        <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
                          {matchedSubject.works.map(work => (
                            <FormControlLabel
                              key={work.id}
                              sx={{ display: 'flex', ml: 0 }}
                              control={
                                <Checkbox
                                  checked={selectedWorksInput.includes(work.id)}
                                  onChange={() => toggleSelectedWork(work.id)}
                                />
                              }
                              label={
                                <Typography variant="body2">
                                  {work.title}{work.price != null ? ` — ${work.price} ₽` : ''}
                                </Typography>
                              }
                            />
                          ))}
                        </Box>
                      )}

                      <Typography variant="body2" sx={{ mt: 1, color: '#059669', fontWeight: 600 }}>
                        {isFullCourseInput
                          ? `${calculateFullCoursePrice(matchedSubject)} ₽`
                          : `${calculateSelectedWorksPrice(matchedSubject, selectedWorksInput)} ₽ (${selectedWorksInput.length} работ выбрано)`}
                      </Typography>
                    </Box>
                  );
                }
                return (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Описание / состав работ"
                    value={descriptionInput}
                    onChange={(e) => setDescriptionInput(e.target.value)}
                    helperText="Для этого заказа нет соответствующего предмета в каталоге работ — описание редактируется как текст"
                    sx={{ mb: 2 }}
                  />
                );
              })()}

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Дополнительные требования"
                value={inputDataInput}
                onChange={(e) => setInputDataInput(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                multiline
                rows={2}
                label="Информация о варианте"
                value={variantInfoInput}
                onChange={(e) => setVariantInfoInput(e.target.value)}
                sx={{ mb: 2 }}
              />
              
              {/* Информация об исправлениях */}
              {selectedOrder.status === OrderStatus.NEEDS_REVISION && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="error" gutterBottom>
                    🔄 Запрошены исправления
                  </Typography>
                  {selectedOrder.revision_comment && (
                    <Typography variant="body2" gutterBottom sx={{ mt: 1, wordBreak: 'break-word' }}>
                      <strong>Комментарий:</strong> {selectedOrder.revision_comment}
                    </Typography>
                  )}
                  {selectedOrder.revision_grade && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Оценка из Moodle:</strong> {selectedOrder.revision_grade}
                    </Typography>
                  )}
                </Box>
              )}
              
              <Box sx={{ mt: 3 }}>
                {/* Стоимость заказа и выплаты */}
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                      label="Стоимость, ₽"
                      type="number"
                      value={priceInput}
                      onChange={(e) => {
                        const newPrice = e.target.value;
                        setPriceInput(newPrice);
                        const parsed = parseFloat(newPrice.replace(',', '.'));
                        if (!isNaN(parsed) && parsed >= 0) {
                          setPayoutInput(String(Math.round(parsed * 0.8)));
                        }
                      }}
                      sx={{ maxWidth: 200 }}
                      inputProps={{ step: '50', min: '0' }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ minWidth: 0, px: 1, py: 0.5, fontSize: '0.7rem', whiteSpace: 'nowrap' }}
                      onClick={() => {
                        const parsed = parseFloat(priceInput.replace(',', '.'));
                        if (!isNaN(parsed) && parsed >= 0) {
                          const newPrice = Math.round(parsed * 1.25);
                          setPriceInput(String(newPrice));
                          setPayoutInput(String(Math.round(newPrice * 0.8)));
                        }
                      }}
                    >
                      +25%
                    </Button>
                  </Box>
                  <TextField
                    label="К выплате, ₽"
                    type="number"
                    value={payoutInput}
                    onChange={(e) => setPayoutInput(e.target.value)}
                    sx={{ maxWidth: 200 }}
                    inputProps={{ step: '50', min: '0' }}
                  />
                  <TextField
                    label="Исполнитель (telegram)"
                    value={executorInput}
                    onChange={(e) => setExecutorInput(e.target.value)}
                    sx={{ maxWidth: 240 }}
                    placeholder="@executor"
                  />
                  <Button variant="outlined" color="error" onClick={() => setExecutorInput('')}>
                    Снять исполнителя
                  </Button>
                </Box>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Реквизиты для оплаты</InputLabel>
                  <Select
                    value={paymentMethodInput}
                    onChange={(e) => setPaymentMethodInput(e.target.value as PaymentMethod)}
                    label="Реквизиты для оплаты"
                  >
                    {Object.entries(paymentMethodLabels).map(([value, label]) => (
                      <MenuItem key={value} value={value}>
                        {label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Статус</InputLabel>
                  <Select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value as OrderStatus)}
                    label="Статус"
                  >
                    {allowedStatuses.map((value) => (
                      <MenuItem key={value} value={value}>
                        {statusLabels[value]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {!selectedOrder.is_paid && (
                    <Button
                      variant="outlined"
                      color="success"
                      onClick={handleMarkAsPaid}
                    >
                      Отметить как оплаченный
                    </Button>
                  )}
                  
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                  >
                    {uploading ? 'Загрузка...' : 'Загрузить файлы'}
                    <input
                      type="file"
                      hidden
                      multiple
                      accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.xls,.xlsx,.csv,.ods,.ppt,.pptx,.odp,.zip,.rar,.7z,.tar,.gz,.bz2,.jpg,.jpeg,.png,.gif,.bmp,.svg,.tiff,.py,.js,.html,.css,.json,.xml,.yaml,.yml,.cpp,.c,.java,.php,.rb,.go,.rs,.swift,.md,.log"
                      onChange={handleFileUpload}
                    />
                  </Button>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={handleSetWaitingPayment}
                  >
                    Поставить "Ожидание оплаты" с выбранными реквизитами
                  </Button>
                </Box>
                
                {selectedOrder.files && selectedOrder.files.length > 0 && (
                  <Box>
                    <Typography variant="body2" gutterBottom>
                      Загруженные файлы:
                    </Typography>
                    {selectedOrder.files.map((file, index) => (
                      <Typography key={index} variant="body2" color="primary" sx={{ wordBreak: 'break-all' }}>
                        📎 {file}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDialogOpen(false)}>
            Отмена
          </Button>
          <Button onClick={() => handleSaveAdmin()} variant="contained">
            Сохранить изменения
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPage;